/** biome-ignore-all lint/correctness/useUniqueElementIds: unique login component */
/** biome-ignore-all lint/a11y/useValidAnchor: custom link */
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  ButtonUI,
  Checkbox,
  Input,
  Selector,
  SelectorOptionPropsInterface,
  showNotification,
  useQueryFetch,
  validationErrorChecker
} from 'sonata-react-components';
import { countriesDictionary } from 'sonata-static-storage/lib/samples/countries.samples';

import { signUpUser } from '@/services/authentication.service';
import { getSignupSchema, type SignupFormValues } from '@/validators/user.validator';

import './styles.scss';

type State = SignupFormValues;

const termsAndConditionsLink =
  'https://cdn.prod.website-files.com/5fdc7d40015e9e207219e12f/67aa2bdf61f5a26d657e99b9_2025%2001%2001%20Taptap%20Platform.%20General%20T%26C_v25_01_ENG-ESP.pdf';

export default function SignUpPage() {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const signUpSchema = getSignupSchema(formatMessage);
  const [state, setState] = useState<State>({
    acceptedTerms: false,
    companyName: '',
    country: '',
    email: '',
    password: '',
    repeatPassword: ''
  });
  const { acceptedTerms, companyName, country, email, password, repeatPassword } = state;

  const { isFetching, refetch } = useQueryFetch(
    [email, password, companyName, country, repeatPassword, acceptedTerms],
    async () => {
      await signUpUser(state);
      navigate('/login');
    },
    {
      enabled: false,
      onError: (error: any) => {
        const errorMsg = error.response?.data?.error?.message || '';
        showNotification(
          'error',
          errorMsg.includes('cpyEmailUnique')
            ? formatMessage({
                defaultMessage: 'Email already in use',
                id: 'signUp.emailInUseErrorNotification'
              })
            : formatMessage({
                defaultMessage: 'Error signing up user',
                id: 'signUp.errorNotification'
              })
        );
      }
    }
  );

  const countryOptions = Object.entries(countriesDictionary).map(([id, country]) => ({
    id,
    label: country.name
  }));

  const handleEmailChange = (newEmail: string) => {
    setState(prevState => ({ ...prevState, email: newEmail }));
  };

  const handleCompanyNameChange = (newCompanyName: string) => {
    setState(prevState => ({ ...prevState, companyName: newCompanyName }));
  };

  const handleCountryChange = (countryOption: SelectorOptionPropsInterface) => {
    setState(prevState => ({ ...prevState, country: countryOption.id.toString() }));
  };

  const handlePasswordChange = (newPassword: string) => {
    setState(prevState => ({ ...prevState, password: newPassword }));
  };

  const handleRepeatPasswordChange = (newRepeatPassword: string) => {
    setState(prevState => ({ ...prevState, repeatPassword: newRepeatPassword }));
  };

  const handleAcceptedTermsChange = (isChecked: boolean) => {
    setState(prevState => ({ ...prevState, acceptedTerms: isChecked }));
  };

  const handleTermsAndConditionsClick = () => {
    window.open(termsAndConditionsLink, '_blank');
  };

  const handleSignUp = () => {
    refetch();
  };

  const handleCancel = () => {
    navigate('/login');
  };

  const errors = validationErrorChecker(state, signUpSchema);

  return (
    <div className="signUp">
      <h1 className="signUp__title">
        {formatMessage({
          defaultMessage: 'Let’s get started!',
          id: 'signUp.title'
        })}
      </h1>

      <form className="login__form">
        <Input
          description={email && errors?.email?.message}
          id="email"
          inputAdditionalProps={{
            autoComplete: 'email'
          }}
          inputStatus={email && errors?.email ? 'wrong' : 'default'}
          label={formatMessage({
            defaultMessage: 'Email',
            id: 'signUp.emailLabel'
          })}
          name="email"
          onFinishEdit={textInput => handleEmailChange(textInput as string)}
          placeholder={formatMessage({
            defaultMessage: 'Enter your email',
            id: 'signUp.emailPlaceholder'
          })}
          shape="squareUI"
          type="text"
        />
        <div className="signUp__formRow">
          <Input
            description={companyName && errors?.companyName?.message}
            id="company"
            inputStatus={companyName && errors?.companyName ? 'wrong' : 'default'}
            label={formatMessage({
              defaultMessage: 'Company',
              id: 'signUp.companyLabel'
            })}
            name="companyName"
            onFinishEdit={textInput => handleCompanyNameChange(textInput as string)}
            placeholder={formatMessage({
              defaultMessage: 'Enter company name',
              id: 'signUp.companyPlaceholder'
            })}
            shape="squareUI"
            type="text"
          />
          <Selector
            id="country"
            label={formatMessage({
              defaultMessage: 'Country',
              id: 'signUp.countryLabel'
            })}
            onChange={handleCountryChange}
            options={countryOptions}
            placeholder={formatMessage({
              defaultMessage: 'Select your country',
              id: 'signUp.countryPlaceholder'
            })}
            type="simple"
            value={countryOptions.filter(option => option.id === country)}
          />
        </div>
        <div className="signUp__formRow">
          <Input
            description={password && errors?.password?.message}
            id="password"
            inputStatus={password && errors?.password ? 'wrong' : 'default'}
            label={formatMessage({
              defaultMessage: 'Password',
              id: 'signUp.passwordLabel'
            })}
            name="password"
            onFinishEdit={textInput => handlePasswordChange(textInput as string)}
            placeholder={formatMessage({
              defaultMessage: 'Enter your password',
              id: 'signUp.passwordPlaceholder'
            })}
            shape="squareUI"
            type="password"
          />
          <Input
            description={repeatPassword && errors?.repeatPassword?.message}
            id="repeatPassword"
            inputStatus={repeatPassword && errors?.repeatPassword ? 'wrong' : 'default'}
            label={formatMessage({
              defaultMessage: 'Repeat your password',
              id: 'signUp.repeatPasswordLabel'
            })}
            name="repeatPassword"
            onFinishEdit={textInput => handleRepeatPasswordChange(textInput as string)}
            placeholder={formatMessage({
              defaultMessage: 'Enter your password',
              id: 'signUp.passwordPlaceholder'
            })}
            shape="squareUI"
            type="password"
          />
        </div>
        <div className="signUp__options">
          <Checkbox
            active={acceptedTerms}
            id="acceptTermsAndConditions"
            labelText={formatMessage({
              defaultMessage: 'I accept the',
              id: 'signUp.acceptTermsAndConditionsLabel'
            })}
            name="acceptTermsAndConditions"
            onClickAction={(_, isChecked) => handleAcceptedTermsChange(isChecked)}
          />
          <ButtonUI
            className="signUp__termsAndConditionsLink"
            id="signUp-termsAndConditions-link"
            onClick={handleTermsAndConditionsClick}
            shape="link"
            text={formatMessage({
              defaultMessage: 'Terms & Conditions',
              id: 'signUp.termsAndConditions'
            })}
            type="secondary"
          />
        </div>
        <div className="signUp__actions">
          <ButtonUI
            className="signUp__button"
            id="signUp-button"
            loading={isFetching}
            onClick={handleCancel}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Cancel',
              id: 'signUp.cancelButton'
            })}
            type="secondary"
          />
          <ButtonUI
            className="signUp__button"
            disabled={Object.keys(errors || {}).length > 0}
            id="signUp-button"
            loading={isFetching}
            onClick={handleSignUp}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Join Sonata',
              id: 'signUp.signUpButton'
            })}
            type="primary"
          />
        </div>
      </form>
    </div>
  );
}
